import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell } from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

export const NotificationBell: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Fetch recent unread notifications
  const { data: notifications } = useQuery({
    queryKey: ['notifications-bell', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user?.id)
        .eq('read', false)
        .order('sent_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
    refetchInterval: 60000, // Refetch every 60 seconds
  });

  const unreadCount = notifications?.length || 0;

  const handleNotificationClick = async (n: any) => {
    try {
      await supabase.from('notifications').update({ read: true }).eq('id', n.id);
    } catch (e) {
      console.error('Failed to mark notification as read', e);
    }
    if (n.related_id) {
      navigate(`/project/${n.related_id}`);
    } else {
      navigate('/notifications');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_bid':
        return '💼';
      case 'new_message':
        return '💬';
      case 'project_status_change':
        return '📊';
      case 'deliverable_upload':
        return '📎';
      case 'new_review':
        return '⭐';
      case 'payment_status':
        return '💳';
      default:
        return '🔔';
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute ltr:-top-1 ltr:-right-1 rtl:-top-1 rtl:-left-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between p-2">
          <h6 className="font-semibold">Notifications</h6>
          {unreadCount > 0 && (
            <Badge variant="secondary">{unreadCount} new</Badge>
          )}
        </div>
        <DropdownMenuSeparator />
        
        {notifications?.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            No new notifications
          </div>
        ) : (
          <>
            {notifications?.map((notification) => (
              <DropdownMenuItem 
                key={notification.id} 
                className="p-3 cursor-pointer"
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start ltr:gap-2 rtl:gap-2 rtl:flex-row-reverse w-full">
                  <span className="text-lg">{getNotificationIcon(notification.type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDistanceToNow(new Date(notification.sent_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-center cursor-pointer"
              onClick={() => navigate('/notifications')}
            >
              View all notifications
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};